<template>
	<section v-if="item"
			 ref="sectionRef"
			 class="flex-module container-fluid flexible-singlevideo">
		<div class="row">
			<div class="flexible-singlevideo-wrapper col-xs-12 col-sm-10 col-sm-offset-1">
				<figure>
					<div class="video-wrapper">
						<BaseVideo :data="item.video"
								   class="single-video"
								   :controls="controls"
								   preload
								   autoplay
								   muted
								   playsinline
								   data-cursor-custom
								   :data-cursor-text="indicatorText"
								   data-text-instant
								   @click.prevent="toggleVideo" />
					</div>

					<div class="controls">
						<button class="play"
								@click.prevent="toggleVideo">
							{{ indicatorText }}
						</button>
						<div class="timeline">
							<div class="bar">
								<div class="inner" />
							</div>
						</div>
						<button class="fullscreen"
								@click.prevent="toggleMute">
							{{ videoMutedStatus }}
						</button>
						<button v-show="!firstPlay"
								class="fullscreen"
								@click.prevent="toggleFullscreen">
							FULLSCREEN
						</button>
					</div>
				</figure>

				<div v-if="indicatorActive"
					 class="indicator"
					 data-cursor-custom
					 :data-cursor-text="indicatorText"
					 data-text-instant
					 @click.prevent="toggleVideo">
					<div class="indicator-inner">
						<span>
							{{ indicatorText }}
						</span>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

<script setup>
const props = defineProps({
	data: {
		type: Object,
		required: true,
	}
})

const { gsap } = useGsap()
const { $bus } = useNuxtApp()

const sectionRef = ref(null)

const indicatorText = ref('PLAY')
const indicatorActive = ref(true)
const videoMuted = ref(true)
const controls = ref(null)
const firstPlay = ref(true)
let $q, video

const item = computed(() => props.data?.item)

const videoMutedStatus = computed(() => videoMuted.value ? 'UNMUTE' : 'MUTE')

function firstUnmutePlay() {
	video.muted = false
	videoMuted.value = false
	video.currentTime = 0

	video.play()

	indicatorText.value = 'PAUSE'
	indicatorActive.value = false
	firstPlay.value = false
}

function toggleVideo() {
	if (videoMuted.value && firstPlay.value) {
		firstUnmutePlay()
	}
	else if (video.paused) {
		video.play()
		indicatorText.value = 'PAUSE'
		indicatorActive.value = false
	} else {
		video.pause()
		indicatorText.value = 'PLAY'
		indicatorActive.value = true
	}

	$bus.$emit('cursor-text', indicatorText.value)
}

function toggleMute() {
	if (videoMuted.value && firstPlay.value) {
		videoMuted.value = false
		firstUnmutePlay()
		return
	} else {
		video.muted = !video.muted
		videoMuted.value = video.muted
	}
}

function toggleFullscreen() {
	if (video.requestFullscreen) {
		video.requestFullscreen()
	} else if (video.mozRequestFullScreen) {
		video.mozRequestFullScreen()
	} else if (video.webkitRequestFullscreen) {
		video.webkitRequestFullscreen()
	} else if (video.webkitEnterFullscreen) {
		video.webkitEnterFullscreen()
	} else if (video.msRequestFullscreen) {
		video.msRequestFullscreen()
	} else {
		console.log('Fullscreen API not supported')
	}
}

function initComponent() {
	$q = gsap.utils.selector(sectionRef.value)
	video = $q('video')[0]
}

onMounted(() => {
	useAnims(initComponent)
})
</script>

<style lang="scss" scoped>
.flexible-singlevideo-wrapper {
	display: flex;
	position: relative;
	transition: visibility 0.4s ease-in-out;

	.single-video {
		&:hover {
			cursor: pointer;
		}
	}

	&:hover {
		.indicator {
			visibility: hidden;
		}
	}

	.indicator {
		width: 67px;
		height: 67px;
		position: absolute;
		top: 50%;
		left: 50%;
		background-color: var(--cursor-background-color, $black);
		border-radius: 999px;
		transform: translate(-50%, -50%);
		pointer-events: none;
		z-index: 500;
		cursor: pointer;

		&:before {
			content: '';
			position: absolute;
			height: 100%;
			width: 100%;
			left: 0;
			top: 0;
			// opacity: 0;
			border: 5px solid var(--cursor-background-color, $black);
			background-color: var(--cursor-background-color, $black);
			border-radius: 999px;
			// z-index: 999;
			-webkit-animation: pulsate 2s ease-out;
			-webkit-animation-iteration-count: infinite;
		}

		.indicator-inner {
			position: absolute;
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%);
			border-radius: 50%;
			background-color: var(--cursor-background-color, $black);
			color: var(--cursor-foreground-color, $white)
		}
	}
}

figure {
	position: relative;
	display: flex;
	width: 100%;
	justify-content: center;
	align-items: center;

	.video-wrapper {
		@include rounded-corners;

		video {
			display: block;
			width: 100%;
			height: auto;
		}
	}

	.controls {
		position: absolute;
		left: 20px;
		@include textBody;
		bottom: 0;
		width: calc(100% - 40px);
		display: flex;
		justify-content: space-around;
		opacity: 0.2;
		transition: opacity 0.4s;
		z-index: 500;

		button {
			background: transparent;
			color: gray;
			font-weight: bolder;
			text-transform: uppercase;
			padding: rem-calc(20px 10px);
			// text-shadow: 2px 1px 2px #000;
			mix-blend-mode: difference;
			cursor: pointer;
		}

		.timeline {
			position: relative;
			flex: 1;
			display: flex;
			align-items: center;
			border: none;
			padding-left: 20px;
			padding-right: 20px;

			// &::before,
			// &::after {
			// 	content: '';
			// 	position: absolute;
			// 	top: 50%;
			// 	left: 20px;
			// 	transform: translateY(-50%);
			// 	background-color: var(--background-color, $white);
			// 	width: rem-calc(1);
			// 	height: rem-calc(10);
			// }

			// &::after {
			// 	left: unset;
			// 	right: 20px;
			// }

			.bar {
				// background: rgb(1, 1, 65);
				// background: var(--background-color, $white);
				height: 0px;
				flex: 1;

				.inner {
					background: var(--foreground-color, $white);
					width: 0%;
					height: 100%;
				}
			}
		}
	}

	&:hover .controls {
		opacity: 1;
	}
}

@keyframes pulsate {
	0% {
		transform: scale(0.1, 0.1);
		opacity: 0.0;
	}

	50% {
		opacity: 1.0;
	}

	100% {
		transform: scale(1.5, 1.5);
		opacity: 0.0;
	}
}
</style>
